import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CircleDollarSignIcon, Clock3Icon, DropletsIcon, LayersIcon, Maximize2Icon, Minimize2Icon, PanelRightIcon, RotateCwIcon } from 'lucide-react';
import { InfoTip } from './ui/InfoTip';
import { TouchpointIcon } from './icons/ToolIcons';
type SummaryCardProps = {
  supportsGenerated: boolean;
  touchpoints: number;
};
function Caret({
  open
}: {
  open: boolean;
}) {
  return <svg viewBox="0 0 8 5" className={`h-[4px] w-[7px] transition-transform duration-150 ${open ? '' : '-rotate-90'}`} fill="currentColor" aria-hidden="true" data-id="element-766" data-mp="3g2bsdjqco26s">
      <path d="M0 0 H8 L4 5 Z" data-id="element-767" data-mp="3ps53ykqhshb1" />
    </svg>;
}
function EnterInfo() {
  return <button type="button" className="text-[12px] text-pf-blue underline underline-offset-2 hover:text-pf-blue-hover" data-id="element-768" data-mp="3l0mzvnt6a0lu">
      Enter Info
    </button>;
}
export function SummaryCard({
  supportsGenerated,
  touchpoints
}: SummaryCardProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [volumeOpen, setVolumeOpen] = useState(true);
  const [costOpen, setCostOpen] = useState(true);
  const row = 'flex h-[19px] items-center justify-between text-[12px] text-pf-ink';
  const sub = `${row} pl-[20px]`;
  return <section aria-label="Summary" className="absolute bottom-[10px] left-[7px] w-[255px] rounded-[6px] bg-[#f4f5f7]/95 px-[11px] pb-[8px] pt-[8px] shadow-[0_2px_8px_rgba(20,28,38,0.12)]" data-id="element-769" data-mp="1g5goggql6vpt">
      <div className="flex items-center justify-between pb-[5px]" data-id="element-770" data-mp="ngjbmt559r4g">
        <div className="flex items-center gap-[5px]" data-id="element-771" data-mp="set7z72luxrn">
          <h2 className="text-[13.5px] font-bold text-pf-ink" data-id="element-772" data-mp="sfcsmvjn409d">Summary</h2>
          <InfoTip label="About summary" data-id="element-773" data-mp="xdo0j0qdk0oy" />
        </div>
        <div className="flex items-center gap-[12px] text-[#4b5056]" data-id="element-774" data-mp="xgaa15wtsq9t">
          <button type="button" aria-label="Dock summary to sidebar" className="hover:text-pf-ink" data-id="element-775" data-mp="11h2hpuq1un3z">
            <PanelRightIcon className="h-[11px] w-[11px]" strokeWidth={2.2} data-id="element-776" data-mp="28st8aj5d4pff" />
          </button>
          <button type="button" aria-label={collapsed ? 'Expand summary' : 'Collapse summary'} onClick={() => setCollapsed(c => !c)} className="hover:text-pf-ink" data-id="element-777" data-mp="2ct3dhtlbzbt8">
            {collapsed ? <Maximize2Icon className="h-[11px] w-[11px]" strokeWidth={2.2} data-id="element-778" data-mp="8ps3vo0kgv7s" /> : <Minimize2Icon className="h-[11px] w-[11px]" strokeWidth={2.2} data-id="element-779" data-mp="mqcjkbb6qax2" />}
          </button>
        </div>
      </div>
      <AnimatePresence initial={false} data-id="element-780" data-mp="hr9qlp8hbv5g">
        {!collapsed && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-781" data-mp="3q7b930xearix">
          
            <div className="flex flex-col gap-[3px]" data-id="element-782" data-mp="3tthg5ystna0">
              <div className={row} data-id="element-783" data-mp="3v663z18n06je">
                <span className="flex items-center gap-[7px]" data-id="element-784" data-mp="3v53bwpzkvy1w">
                  <Clock3Icon className="h-[12px] w-[12px] text-[#4b5056]" strokeWidth={2.4} data-id="element-785" data-mp="37fs78xf3xjih" />
                  Time Estimate <InfoTip label="About time estimate" data-id="element-786" data-mp="32gz33vlpn4uz" />
                </span>
                <span className="flex items-center gap-[6px] text-[#4b5056]" data-id="element-787" data-mp="1hzg50jw6ru5b">
                  {supportsGenerated ? '1 hr 12 min' : '--'}
                  <RotateCwIcon className="h-[11px] w-[11px]" strokeWidth={2.2} data-id="element-788" data-mp="1mrpr1ou6ka2b" />
                </span>
              </div>
              <button type="button" aria-expanded={volumeOpen} onClick={() => setVolumeOpen(o => !o)} className={row} data-id="element-789" data-mp="18x6wfzkhaauk">
                <span className="flex items-center gap-[7px]" data-id="element-790" data-mp="21n5q689maw5k">
                  <DropletsIcon className="h-[12px] w-[12px] text-[#4b5056]" strokeWidth={2.4} data-id="element-791" data-mp="21lku2e7nn55l" />
                  Volume
                </span>
                <span className="flex items-center gap-[8px]" data-id="element-792" data-mp="1rqastsp2z1sv">
                  {supportsGenerated ? '0.14 mL' : '< 0.10 mL'} <Caret open={volumeOpen} data-id="element-793" data-mp="1wnjcjxpvndw8" />
                </span>
              </button>
              {volumeOpen && <>
                  <div className={`${sub} pr-[15px]`} data-id="element-794" data-mp="g91d9h92otwu"><span data-id="element-795" data-mp="l95uxalpvuon">Model(s)</span><span data-id="element-796" data-mp="l9p9qig2m2yg">&lt; 0.10 mL</span></div>
                  <div className={`${sub} pr-[15px]`} data-id="element-797" data-mp="l7cl5mft4ucf"><span data-id="element-798" data-mp="10jm0o6239gwh">Supports</span><span data-id="element-799" data-mp="10k43xmn64vdw">{supportsGenerated ? '0.03 mL' : '0 mL'}</span></div>
                  <div className={`${sub} pr-[15px]`} data-id="element-800" data-mp="vru7dnrjsx2n"><span data-id="element-801" data-mp="vrkhe06i6pwy">Raft</span><span data-id="element-802" data-mp="vr2o9cf50rt9">{supportsGenerated ? '0.01 mL' : '0 mL'}</span></div>
                </>}
              <button type="button" aria-expanded={costOpen} onClick={() => setCostOpen(o => !o)} className={`${row} mt-[2px]`} data-id="element-803" data-mp="roo4mq3n9o0x">
                <span className="flex items-center gap-[7px]" data-id="element-804" data-mp="1jhohhxkhg7j7">
                  <CircleDollarSignIcon className="h-[12px] w-[12px] text-[#4b5056]" strokeWidth={2.4} data-id="element-805" data-mp="1jfu8nhmhk5vh" />
                  Total Print Cost <InfoTip label="About total print cost" data-id="element-806" data-mp="1ah9vsx3lwqf6" />
                </span>
                <span className="flex items-center gap-[8px] text-pf-blue underline underline-offset-2" data-id="element-807" data-mp="1ajcus6mqltw1">
                  Enter Info <span className="text-pf-ink" data-id="element-808" data-mp="3v7hfuw8ya0rx"><Caret open={costOpen} data-id="element-809" data-mp="3v7r4afecjzvi" /></span>
                </span>
              </button>
              {costOpen && <>
                  <div className={`${sub} pr-[15px]`} data-id="element-810" data-mp="3q735h7jvmeqq"><span data-id="element-811" data-mp="3g4ppdpxcq54u">Model Cost</span><EnterInfo data-id="element-812" data-mp="3g58xyg143h67" /></div>
                  <div className={`${sub} pr-[15px]`} data-id="element-813" data-mp="3g6alhjzbdw4t">
                    <span className="flex items-center gap-[5px]" data-id="element-814" data-mp="3l30w3rscnziu">Support &amp; Raft Cost <InfoTip label="About support and raft cost" data-id="element-815" data-mp="3l17uiqwlg5hp" /></span>
                    <EnterInfo data-id="element-816" data-mp="3q1cb0gxi507x" />
                  </div>
                </>}
              <div className={row} data-id="element-817" data-mp="3uuwy3dxk49sc">
                <span className="flex items-center gap-[7px]" data-id="element-818" data-mp="3uv6nazas2cwz">
                  <TouchpointIcon className="h-[12px] w-[12px] text-[#4b5056]" data-id="element-819" data-mp="2p1gf33ljw0m" />
                  Touchpoints
                </span>
                <span data-id="element-820" data-mp="7qh0osxy9yhy">{touchpoints}</span>
              </div>
              <div className={row} data-id="element-821" data-mp="coa8fqewj0kc">
                <span className="flex items-center gap-[7px]" data-id="element-822" data-mp="clwc7e8hjib7">
                  <LayersIcon className="h-[12px] w-[12px] text-[#4b5056]" strokeWidth={2.4} data-id="element-823" data-mp="hmbr4a8f0xva" />
                  Layers
                </span>
                <span data-id="element-824" data-mp="2je71mmixqwbx">{supportsGenerated ? 129 : 29}</span>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}